<template>
  <div>
    <v-card-title>
      <v-select
        v-model="selectedTypes"
        class="ml-5"
        :items="types()"
        item-key="id"
        item-text="name"
        item-value="id"
        :menu-props="{ maxHeight: '400' }"
        label="Types"
        multiple
      >
        <template v-slot:selection="{ item, index }">
          <span v-if="index < maxDisplay">{{ item.name }} &nbsp;</span>
          <span
            v-if="index === maxDisplay"
            class="grey--text caption"
          >(+{{ selectedTypes.length - maxDisplay }} types)</span>
        </template>
      </v-select>
      <v-text-field
        v-model="search"
        class="ml-5"
        append-icon="search"
        label="Filter"
      />
    </v-card-title>

    <v-tooltip
      top
      dark
    >
      <template v-slot:activator="{ on }">
        <v-btn
          color="secondary"
          fab
          dark
          absolute
          top
          right
          @click.stop="() => showDownloadCashbackV2Dialog()"
          v-on="on"
        >
          <v-icon>file_download</v-icon>
        </v-btn>
      </template>
      <span>Taxable Cashback CSV for Elster Upload Filler</span>
    </v-tooltip>

    <v-tooltip
      top
      dark
    >
      <template v-slot:activator="{ on }">
        <v-btn
          color="secondary"
          fab
          dark
          absolute
          top
          left
          @click="refresh(selectedTypes)"
          v-on="on"
        >
          <v-icon>refresh</v-icon>
        </v-btn>
      </template>
      <span>Refresh</span>
    </v-tooltip>

    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :pagination.sync="pagination"
      :loading="isLoading"
      loading-text="Loading..."
      disable-initial-sort
      :rows-per-page-items="[25,50,{'text':'$vuetify.dataIterator.rowsPerPageAll','value':-1}]"
    >
      <template
        slot="items"
        slot-scope="props"
      >
        <tr
          @click="toggleSelected(props.item)"
        >
          <td>
            {{ props.item.description }}
          </td>
          <td>
            {{ getGrossAmount(props.item.holders)|toCurrency }}
          </td>
          <td>
            {{ getNettoAmount(props.item.holders)|toCurrency }}
          </td>
          <td>
            {{ parseFloat(props.item.amount)|toCurrency }}
          </td>
          <td>
            {{ props.item.security_id }}
          </td>
          <td>
            {{ types()[props.item.type] }}
          </td>
          <td>
            <DateDisplay
              :date="props.item.expected_payout_date"
            />
          </td>
          <td class="text-xs">
            <table>
              <tr>
                <td>
                  <v-tooltip
                    top
                    dark
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        dark
                        flat
                        icon
                        color="primary"
                        @click.stop="() => downloadCsv(props.item.payout_id)"
                        v-on="on"
                      >
                        <v-icon>download</v-icon>
                      </v-btn>
                    </template>
                    <span>Download Holder List CSV</span>
                  </v-tooltip>
                </td>
                <td>
                  <v-tooltip
                    top
                    dark
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        dark
                        flat
                        icon
                        color="primary"
                        @click.stop="() => downloadPayoutInvestorBankTransferCsv(props.item.payout_id)"
                        v-on="on"
                      >
                        <v-icon>euro</v-icon>
                      </v-btn>
                    </template>
                    <span>Download Bank Transfer CSV</span>
                  </v-tooltip>
                </td>
                <td>
                  <v-tooltip
                    top
                    dark
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        dark
                        flat
                        icon
                        color="primary"
                        @click.stop="() => downloadPayoutChurchTaxCsv(props.item.payout_id)"
                        v-on="on"
                      >
                        <v-icon>church</v-icon>
                      </v-btn>
                    </template>
                    <span>Download CSV for Elster Upload Filler</span>
                  </v-tooltip>
                </td>
                <td
                  v-if="showGoToPaybackOverview(props.item)"
                >
                  <v-tooltip
                    top
                    dark
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        dark
                        flat
                        icon
                        color="primary"
                        @click.stop="() => goToPaybackOverview(props.item.payout_id)"
                        v-on="on"
                      >
                        <v-icon>currency_exchange</v-icon>
                      </v-btn>
                    </template>
                    <span>Go to Payback Overview</span>
                  </v-tooltip>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <tr v-if="props.item.payout_id && selected === props.item.payout_id">
          <td colspan="11">
            <PayoutDetails
              :payout-id="props.item.payout_id"
              :payout="props.item"
              :holders="props.item.holders"
              :show-holders="true"
              :show-holders-address="true"
              :show-holders-payback-information="true"
              :show-address-retry-action="false"
              :show-pdf-download-action="true"
            />
          </td>
        </tr>
      </template>
    </v-data-table>
    <v-snackbar
      v-model="snackbarShow"
      :color="snackbarColor"
      :auto-height="true"
    >
      <ul v-if="snackbarErrors">
        <li
          v-for="snackbarError in snackbarErrors"
          :key="snackbarError.message"
        >
          {{ snackbarError.message }}
        </li>
      </ul>

      <div
        v-if="snackbarMessage"
      >
        {{ snackbarMessage }}
      </div>

      <v-btn
        color="primary"
        text
        @click="snackbarShow = false"
      >
        Close
      </v-btn>
    </v-snackbar>
    <DownloadCashbackV2Dialog
      :visible="downloadCashbackV2Dialog"
      :toggle-open="onDownloadCashbackV2"
    />
  </div>
</template>

<script>

import PayoutDetails from '@/components/payoutsAndTaxes/payout/shared/PayoutDetails';
import DateDisplay from '@/components/payoutsAndTaxes/payout/shared/DateDisplay';
import { downloadFromS3 } from '@/helpers/axiosS3Downloader';
import { downloadCsvFile } from '@/helpers';
import DownloadCashbackV2Dialog from '@/components/payoutsAndTaxes/payout/dialogs/payout/DownloadCashbackV2Dialog.vue';

export default {
  name: 'ApprovedPayouts',
  components: {
    DownloadCashbackV2Dialog,
    PayoutDetails,
    DateDisplay,
  },
  data() {
    return {
      snackbarShow: false,
      snackbarMessage: '',
      selectedTypes: ['quarterly_interest'],
      maxDisplay: 2,
      snackbarColor: 'error',
      snackbarErrors: [],
      selected: 0,
      isLoading: false,
      pagination: {
        rowsPerPage: 25,
      },
      search: '',
      headers: [
        { text: 'Payout', value: 'description' },
        { text: 'Amount (Gross)', value: 'gross_amount' },
        { text: 'Amount (After taxes)', value: 'netto_amount' },
        { text: 'Amount', value: 'amount' },
        { text: 'Security ID', value: 'security_id' },
        { text: 'Type', value: 'type' },
        { text: 'Expected Payout Date', value: 'expected_payout_date' },
        { text: 'Actions', value: 'actions' },
      ],
      downloadCashbackV2Dialog: false,
    };
  },
  computed: {
    payoutFinalCheckPendingApprovals() {
      return this.$store.getters.getPayoutsFinalCheckApprovalProcessed;
    },
    filteredItems() {
      const { search } = this;

      return search.length < 1 ?
        this.payoutFinalCheckPendingApprovals :
        this.payoutFinalCheckPendingApprovals.filter((item) => {
          let found = false;
          Object.keys(item)
            .map((key) => {
              if (item[key] && String(item[key]).toLowerCase().indexOf(search.toLowerCase()) > -1) {
                found = true;
              }

              return key;
            });

          return found;
        });
    },
  },
  watch: {
    selectedTypes: {
      handler(val, oldVal) {
        this.refresh(val.join(','));
      },
    },
  },
  mounted() {
    this.$store.dispatch('fetchPayoutCreationTypes');

    this.refresh(this.selectedTypes);
  },
  methods: {
    refresh(types = '') {
      this.$store.dispatch('fetchPayoutFinalCheckApprovalProcessed', types);
    },
    types() {
      const data = this.$store.getters.getPayoutsCreationTypes;

      if (!data || !data.payout || !data.payout.types) {
        return [];
      }

      return this.transformEnumToSelectArray(data.payout.types);
    },
    transformEnumToSelectArray(options) {
      const newOptions = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const [key, value] of Object.entries(options)) {
        newOptions.push({
          id: key,
          name: value,
        });
      }

      return newOptions;
    },
    getGrossAmount(holders) {
      if (!holders) {
        return 'Expand to calculate';
      }
      const initialSumValue = 0;

      return holders.reduce(
        (sum, holder) => sum + holder.payback_information.gross_amount.total || 0,
        initialSumValue,
      );
    },
    getNettoAmount(holders) {
      if (!holders) {
        return 'Expand to calculate';
      }
      const initialSumValue = 0;

      return holders.reduce(
        (sum, holder) => sum + holder.payback_information.net_amount.total || 0,
        initialSumValue,
      );
    },
    downloadCsv(payoutId) {
      this.$store
        .dispatch('fetchPayoutBankTransferDownloadCsv', payoutId)
        .then((response) => {
          downloadFromS3(response.data).then((data) => {
            downloadCsvFile(data, response.data.fileName);
          });
        });
    },
    downloadPayoutInvestorBankTransferCsv(payoutId) {
      this.$store
        .dispatch('fetchPayoutInvestorBankTransferDownloadCsv', payoutId)
        .then((response) => {
          downloadFromS3(response.data).then((data) => {
            downloadCsvFile(data, response.data.fileName);
          });
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    downloadPayoutChurchTaxCsv(payoutId) {
      this.$store
        .dispatch('fetchPayoutChurchTaxDownloadCsv', payoutId)
        .then((response) => {
          downloadFromS3(response.data).then((data) => {
            downloadCsvFile(data, response.data.fileName);
          });
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    showGoToPaybackOverview(payout) {
      const createdAt = new Date(payout.created_at);
      if (createdAt < new Date('2023-08-15')) {
        return false;
      }

      return true;
    },
    async goToPaybackOverview(payoutId) {
      await this.$router.push(`/pat/payback/${payoutId}`);
    },
    toggleSelected(payout) {
      this.selected = this.selected === payout.payout_id ? null : payout.payout_id;
      this.loadPayoutHolders(payout);
    },
    loadPayoutHolders(payout) {
      if (payout.holders && payout.holders.length !== 0) {
        return;
      }

      this.$store.dispatch('fetchPayoutDataGetHolders', {
        origin: 'approvedPayouts',
        payoutId: payout.payout_id,
      })
        .then(() => {
          this.$forceUpdate();
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    showDownloadCashbackV2Dialog() {
      this.downloadCashbackV2Dialog = true;
      this.$forceUpdate();
    },
    onDownloadCashbackV2() {
      this.downloadCashbackV2Dialog = !this.downloadCashbackV2Dialog;
    },
  },
};
</script>
<style lang="scss">
</style>



// WEBPACK FOOTER //
// src/components/payoutsAndTaxes/payout/ApprovedPayouts.vue